import { FC, RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Flex,
	Image,
	Spinner,
	Tooltip,
	useDisclosure,
} from '@chakra-ui/react';
import { ChatMessageEnum, IMessage, ThreadRolesEnum } from 'src/lib/schemas';
import { InfoIcon } from '@chakra-ui/icons';
import { toastError } from 'src/services/toast';
import FusionLoading from 'src/components/common/FusionLoading';
import UserLogo from 'src/components/common/UserLogo';
import ThinkingMessage from '../animation/ThinkingMessage';
import Message from './Message';
import Suggestion from './Suggestion';
import ZoomedImage from './ZoomedImage';
import FusionLogo from 'src/components/common/FusionLogo';
import { useRealtimeChatContext } from '../hooks/useRealtimeChatContext';

interface IMessages {
	conversation: Array<IMessage>;
	scrollContainerRef?: RefObject<HTMLDivElement>;
}

const Messages: FC<IMessages> = ({ conversation, scrollContainerRef }) => {
	const LOGO_SIZES = {
		h: 8,
		w: 8,
	};
	const { shouldShowTypingIndicator, countryConfig, isLoading, setIsLoading, onboardingState, scrollRequestTimestamp } =
		useRealtimeChatContext();

	const lastItemRef = useRef<HTMLDivElement>(null);
	const lastMessageRef = useRef<HTMLDivElement>(null);
	const wasAtBottomRef = useRef<boolean>(true);
	const programmaticScrollRef = useRef<boolean>(false);
	const previousMessageCountRef = useRef<number>(0);
	const previousScrollTimestampRef = useRef<number>(0);

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const [retryingMessageId, setRetryingMessageId] = useState<string>();

	// Check if user is scrolled to bottom (with small threshold for rounding errors)
	const isScrolledToBottom = useCallback(() => {
		if (!scrollContainerRef?.current) return true;
		const container = scrollContainerRef.current;
		// Larger threshold for mobile to account for touch scrolling inertia and bounce
		const threshold = 100; // pixels from bottom to consider "at bottom"
		const scrollPosition = container.scrollTop + container.clientHeight;
		const scrollHeight = container.scrollHeight;
		const isAtBottom = scrollHeight - scrollPosition <= threshold;

		// Additional check: if scrollTop is very close to max, definitely at bottom
		const maxScroll = scrollHeight - container.clientHeight;
		const isAtMaxScroll = maxScroll - container.scrollTop <= threshold;

		return isAtBottom || isAtMaxScroll;
	}, [scrollContainerRef]);

	// Track scroll position to know if we should auto-scroll on new messages
	useEffect(() => {
		const container = scrollContainerRef?.current;
		if (!container) return;

		let scrollTimeout: NodeJS.Timeout | null = null;
		let lastScrollTop = container.scrollTop;

		const handleScroll = () => {
			// Debounce scroll events to avoid false positives from inertia scrolling
			if (scrollTimeout) clearTimeout(scrollTimeout);

			scrollTimeout = setTimeout(() => {
				// Skip if this scroll was triggered programmatically (not by user)
				// This prevents overriding wasAtBottomRef during auto-scroll + corrections
				if (programmaticScrollRef.current) return;

				lastScrollTop = container.scrollTop;
				wasAtBottomRef.current = isScrolledToBottom();
			}, 50);
		};

		container.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			if (scrollTimeout) clearTimeout(scrollTimeout);
			container.removeEventListener('scroll', handleScroll);
		};
	}, [scrollContainerRef, isScrolledToBottom]);

	const handleScrollToBottom = useCallback((instant = false, delay = 0, scrollToEnd = false) => {
		if (!scrollContainerRef?.current) return;

		// Mark that we're scrolling programmatically
		wasAtBottomRef.current = true;
		programmaticScrollRef.current = true;

		const doScroll = () => {
			const container = scrollContainerRef.current;
			if (!container) return;

			if (scrollToEnd) {
				if (instant) {
					container.scrollTop = container.scrollHeight;
				} else {
					container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
				}
			} else if (lastMessageRef.current) {
				const containerRect = container.getBoundingClientRect();
				const messageRect = lastMessageRef.current.getBoundingClientRect();
				const offset = messageRect.top - containerRect.top;
				if (Math.abs(offset) < 10) return;
				const targetScrollTop = container.scrollTop + offset;
				if (instant) {
					container.scrollTop = targetScrollTop;
				} else {
					container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
				}
			} else {
				if (instant) {
					container.scrollTop = container.scrollHeight;
				} else {
					container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
				}
			}
		};

		if (delay > 0) {
			setTimeout(doScroll, delay);
		} else {
			// No rAF wrapper - execute immediately for instant scrolls
			// This prevents the 1-frame flash where wrong content is visible
			doScroll();
		}
	}, [scrollContainerRef]);

	// Helper: directly correct scroll position to show lastMessageRef at container top
	// Used for timed corrections after async widget loading or iOS scroll anchoring shifts
	const correctScrollPosition = useCallback(() => {
		if (!programmaticScrollRef.current) return; // Only correct during programmatic scroll window
		if (!lastMessageRef.current || !scrollContainerRef?.current) return;
		const container = scrollContainerRef.current;
		const containerRect = container.getBoundingClientRect();
		const messageRect = lastMessageRef.current.getBoundingClientRect();
		const offset = messageRect.top - containerRect.top;
		// Only correct if position has actually drifted (>10px)
		if (Math.abs(offset) > 10) {
			container.scrollTop = container.scrollTop + offset;
		}
	}, [scrollContainerRef]);

	// Auto-scroll with useLayoutEffect: sets scroll position BEFORE browser paints
	// This prevents the flash where the bottom of a long message is briefly visible
	useLayoutEffect(() => {
		const hasNewMessage = conversation.length > previousMessageCountRef.current;
		if (!hasNewMessage) {
			previousMessageCountRef.current = conversation.length;
			return;
		}

		const lastMsg = conversation.length > 0 ? conversation[conversation.length - 1] : null;
		const isUserMessage = lastMsg?.role === ThreadRolesEnum.User;
		const isAtBottom = wasAtBottomRef.current;
		const container = scrollContainerRef?.current;

		if (!container) {
			previousMessageCountRef.current = conversation.length;
			return;
		}

		if (isUserMessage) {
			// User message: always scroll to absolute bottom
			container.scrollTop = container.scrollHeight;
			wasAtBottomRef.current = true;
			programmaticScrollRef.current = true;
			setTimeout(() => { programmaticScrollRef.current = false; }, 200);
		} else if (isAtBottom && lastMessageRef.current) {
			// Assistant message: scroll to show message top at container top
			const containerRect = container.getBoundingClientRect();
			const messageRect = lastMessageRef.current.getBoundingClientRect();
			const offset = messageRect.top - containerRect.top;
			if (Math.abs(offset) >= 10) {
				container.scrollTop = container.scrollTop + offset;
			}
			wasAtBottomRef.current = true;
			programmaticScrollRef.current = true;

			// Schedule corrections for async widget loading
			setTimeout(correctScrollPosition, 150);
			setTimeout(correctScrollPosition, 400);
			setTimeout(() => {
				correctScrollPosition();
				programmaticScrollRef.current = false;
			}, 800);
		}

		previousMessageCountRef.current = conversation.length;
	}, [conversation, scrollContainerRef, correctScrollPosition]);

	// Handle typing indicator scroll (separate effect since it doesn't need useLayoutEffect)
	useEffect(() => {
		if (shouldShowTypingIndicator) {
			const lastMsg = conversation.length > 0 ? conversation[conversation.length - 1] : null;
			const isUserMessage = lastMsg?.role === ThreadRolesEnum.User;
			if (isUserMessage) {
				handleScrollToBottom(true, 0, true);
			}
		}
	}, [shouldShowTypingIndicator, conversation, handleScrollToBottom]);

	// Explicit scroll trigger from context (used when message is sent from click handler)
	// This ensures scroll happens even if the normal detection fails due to timing issues
	useEffect(() => {
		if (scrollRequestTimestamp > 0 && scrollRequestTimestamp !== previousScrollTimestampRef.current) {
			previousScrollTimestampRef.current = scrollRequestTimestamp;
			const scrollToBottom = () => {
				if (scrollContainerRef?.current) {
					const container = scrollContainerRef.current;
					container.scrollTop = container.scrollHeight;
					wasAtBottomRef.current = true;
				}
			};

			// Quick scroll after React renders - just 2 attempts to avoid interfering
			// with subsequent message scroll behavior (e.g., bot responses)
			requestAnimationFrame(() => {
				scrollToBottom();
				requestAnimationFrame(scrollToBottom);
			});
		}
	}, [scrollRequestTimestamp, scrollContainerRef]);

	const renderAvatar = useCallback(
		(isUser: boolean) =>
			isUser ? (
				<UserLogo size={LOGO_SIZES} />
			) : (
				<FusionLogo size={LOGO_SIZES} />
			),
		[countryConfig?.avatar],
	);

	const renderAssistantWritting = useMemo(
		() => {
			console.log('[Messages] renderAssistantWritting memoized with currentStep:', onboardingState?.currentStep);
			return (
				<Flex gap={5} alignItems="center">
					{renderAvatar(false)}
					<ThinkingMessage currentStep={onboardingState?.currentStep} />
				</Flex>
			);
		},
		[renderAvatar, onboardingState?.currentStep],
	);

	const handleImageClick = (src: string) => {
		setSelectedImage(src);
		onOpen();
	};

	const ___handleRetryMessage = async (message: IMessage) => {
		try {
			setRetryingMessageId(message.message_id);
			// await ___retryMessage(message);
		} catch (error) {
			toastError(error);
		} finally {
			setRetryingMessageId(undefined);
		}
	};

	const renderSplittedContent = (message: IMessage) => (
		<Flex direction="column" justifyContent="center" gap={2} px={2}>
			{message.splitted_content?.map((content, i) => {
				if (content.type === ChatMessageEnum.Image) {
					return (
						<Box
							key={i}
							borderRadius="md"
							overflow="hidden"
							cursor="pointer"
							onClick={() => handleImageClick(content.value)}
						>
							<Image
								src={content.value}
								alt="User uploaded"
								maxWidth="200px"
								borderRadius="lg"
								objectFit="cover"
							/>
						</Box>
					);
				} else if (content.suggestion) {
					return <Suggestion key={i} content={content.value} />;
				} else {
					return <Message key={i} content={content.value} />;
				}
			})}
		</Flex>
	);

	const renderedMessages = useMemo(() => {
		// Filter out empty messages before rendering (defensive approach)
		const validMessages = conversation.filter((msg) => {
			// Check if message has valid content
			const hasRawMessage = msg.raw_message && msg.raw_message.trim() !== '';
			const hasSplittedContent = msg.splitted_content && msg.splitted_content.length > 0;

			if (!hasRawMessage && !hasSplittedContent) {
				// Only log when actually filtering out empty messages (rare case)
				console.warn('⚠️ [Messages] Filtered empty message:', msg.message_id);
				return false;
			}
			return true;
		});

		// Find the last assistant message to attach the scroll ref
		const lastAssistantIndex = (() => {
			for (let i = validMessages.length - 1; i >= 0; i--) {
				if (validMessages[i].role === ThreadRolesEnum.Assistant) return i;
			}
			return validMessages.length - 1;
		})();

		return validMessages.map((message, i) => {
			const isLastItem = validMessages.length - 1 === i;
			const isUser = message.role === ThreadRolesEnum.User;
			// Attach scroll ref to the last assistant message
			const shouldHaveRef = i === lastAssistantIndex;
			// Check if a user message exists after this message - if so, disable onContentResize
			// to prevent widget loading (e.g. DD images) from overriding the user message scroll
			const hasUserMessageAfter = i < validMessages.length - 1 &&
				validMessages.slice(i + 1).some(m => m.role === ThreadRolesEnum.User);

			return (
				<Flex
					key={message.message_id}
					ref={shouldHaveRef ? lastMessageRef : undefined}
					my={3}
					direction={isUser ? 'row-reverse' : 'row'}
					animation="fadeIn 0.3s ease-out"
					sx={{
						'@keyframes fadeIn': {
							'0%': { opacity: 0 },
							'100%': { opacity: 1 },
						},
					}}
				>
					{renderAvatar(isUser)}
					<Box
						bg={'gray.200'}
						borderRadius={20}
						borderTopLeftRadius={isUser ? 20 : 2}
						borderTopRightRadius={isUser ? 2 : 20}
						p={3}
						mt={3}
						ml={isUser ? 0 : 2}
						mr={isUser ? 2 : 0}
						maxW="80%"
						width="auto"
						overflowWrap="break-word"
						wordBreak="break-word"
						whiteSpace="pre-wrap"
					>
						<Flex direction="column" justifyContent="center" gap={3}>
							{message.raw_message ? (
								<Message
									content={message.raw_message}
									onImageClick={handleImageClick}
									onContentResize={
										// Only trigger scroll for the last assistant message (shouldHaveRef)
										// AND only if no user message came after (prevents widget resize from
										// overriding scroll when user already sent a new message)
										(shouldHaveRef && !hasUserMessageAfter) ? () => {
											handleScrollToBottom(true);
										} : undefined
									}
								/>
							) : (
								renderSplittedContent(message)
							)}
						</Flex>
					</Box>
					{message.error && (
						<Box display="inline-block" alignSelf="center" mx={2}>
							{retryingMessageId && retryingMessageId === message.message_id ? (
								<Spinner size="xs" />
							) : (
								<InfoIcon
									color="red.500"
									cursor="pointer"
									onClick={() => ___handleRetryMessage(message)}
								/>
							)}
						</Box>
					)}
				</Flex>
			);
		});
	}, [conversation, retryingMessageId]);

	// Show full-screen loading only on initial load (no messages yet)
	const showFullLoading = isLoading && conversation.length === 0;

	return (
		<>
			<Flex flex={1} direction="column" p={4} overflowX="hidden" pt={0}>
				{showFullLoading && <FusionLoading isLoading={showFullLoading} />}
				<Box visibility={showFullLoading ? 'hidden' : 'visible'}>
					{renderedMessages}
					{shouldShowTypingIndicator && (
						<Flex my={3}>{renderAssistantWritting}</Flex>
					)}
					<div
						ref={lastItemRef}
						style={{ height: '1px', marginBottom: shouldShowTypingIndicator ? '0px' : '20px' }}
					></div>
				</Box>
			</Flex>

			{selectedImage && (
				<ZoomedImage
					isOpen={isOpen}
					onClose={onClose}
					imageSrc={selectedImage}
				/>
			)}
		</>
	);
};

export default Messages;
